import React, { useState, useEffect, useRef } from "react";
import { Button, Descriptions, Select } from "antd"
import { CloseOutlined } from '@ant-design/icons';
import { Rnd } from "react-rnd";
import { useAtom } from "jotai";
import { isInspectionModalOpenState, inspectionModalFeedState } from "../stateManagement/commonState";
import styles from './InspectionModal.module.css';
import { substanceArray } from "../utils/substancesAndColors";

const InspectionModal = ({ map }) => {
    const [isInspectionModalOpen, setIsInspectionModalOpen] = useAtom(isInspectionModalOpenState);
    const [modalWindowState, setModalWindowState] = useState({
        width: '390px',
        height: '520px',
        x: 10,
        y: 10,
    });
    const [inspectionModalFeed] = useAtom(inspectionModalFeedState);
    const [pointIndex, setPointIndex] = useState(null);
    const [pointLabel, setPointLabel] = useState(null);
    const [predictions, setPredictions] = useState([]);
    const descriptionsComponentRef = useRef(null);
    const [maxHeight, setMaxHeight] = useState(360);
    const mapRef = useRef(null);
    const [sortingKey, setSortingKey] = useState('label');
    const [selectedTraceModel, setSelectedTraceModel] = useState(null);
    const fentanylLabels = ['Acetylfentanyl', 'Carfentanil', 'Despropionyl fentanyl', 'Fentanyl', 'Furanylfentanyl', 'Parafluorofentanyl', 'Remifentanil'];
    const hydromorphoneLabels = ['Hydromorphone'];
    const benzodiazepineLabels = ['Alprazolam', 'Bromazepam', 'Bromazolam', 'Clonazepam', 'Desalkylgidazepam', 'Diazepam', 'Etizolam', 'Flubromazepam', 'Flurazepam', 'Lorazepam', 'Oxazepam', 'Temazepam', 'Triazolam'];
    const xylazineLabels = ['Xylazine', 'Xylazine hydrochloride'];

    useEffect(() => {
        mapRef.current = map;
    }, [map]);

    useEffect(() => {
        if (descriptionsComponentRef.current) {
            const contentHeight = descriptionsComponentRef.current.offsetHeight;
            setMaxHeight(contentHeight + 92);
        }
    }, [descriptionsComponentRef]);

    const sortByKey = (predictions, key) => {
        const sortedPredictions = [...predictions];
        sortedPredictions.sort((a, b) => {
            const aValue = a[key] ?? (key === 'label' ? '' : 0);
            const bValue = b[key] ?? (key === 'label' ? '' : 0);
            return key === 'label'
                ? aValue.localeCompare(bValue)
                : bValue - aValue;
        });
        return sortedPredictions;
    };

    const filterByTraceModel = (predictions, filterByTraceModel) => {
        if (!filterByTraceModel) return predictions;
        return predictions.filter(prediction => {
            if (filterByTraceModel === 'Fentanyl') {
                return fentanylLabels.includes(prediction.label);
            } else if (filterByTraceModel === 'Hydromorphone') {
                return hydromorphoneLabels.includes(prediction.label);
            } else if (filterByTraceModel === 'Benzodiazepine') {
                return benzodiazepineLabels.includes(prediction.label);
            } else if (filterByTraceModel === 'Xylazine') {
                return xylazineLabels.includes(prediction.label);
            }
            return false;
        });
    };

    useEffect(() => {
        if (inspectionModalFeed) {

            const { index, label, result, benzodiazepinePrediction, fentanylPrediction, hydromorphonePrediction, xylazinePrediction } = inspectionModalFeed;
            setPointIndex(index);
            setPointLabel(label);
            const traceLables = [...fentanylLabels, ...hydromorphoneLabels, ...benzodiazepineLabels, ...xylazineLabels];
            const traceValues = [...fentanylPrediction, hydromorphonePrediction[1], ...benzodiazepinePrediction, ...xylazinePrediction];
            const traceResults = Object.fromEntries(
                traceLables.map((label, i) => [label, traceValues[i]])
            );
            const currentPredictions = substanceArray.map((substance, i) => {
                return {
                    label: substance,
                    modelAverage: result[0][i],
                    model1: result[1][i],
                    model2: result[2][i],
                    trace: traceResults[substance],
                };
            });

            setPredictions(sortByKey(filterByTraceModel(currentPredictions, selectedTraceModel), sortingKey));
        }
    }, [inspectionModalFeed, sortingKey, selectedTraceModel]);

    const items = [
        {
            key: 'index',
            label: 'Index',
            span: 2,
            children: <div>{pointIndex ?? '-'}</div>,
        },
        {
            key: 'label',
            label: 'Label',
            span: 2,
            children: <div>{pointLabel ?? '-'}</div>,
        },
        {
            key: 'predictions',
            label: 'Predictions',
            children: <div>
                <Select
                    style={{
                        width: `100%`,
                        marginBottom: '6px',
                        textAlign: 'center',
                    }}
                    dropdownStyle={{
                        textAlign: 'center'
                    }}
                    placeholder="Select trace model"
                    onChange={(e) => setSelectedTraceModel(e)}
                    options={[
                        {
                            value: 'Fentanyl',
                            label: 'Fentanyl',
                        },
                        {
                            value: 'Hydromorphone',
                            label: 'Hydromorphone',
                        },
                        {
                            value: 'Benzodiazepine',
                            label: 'Benzodiazepine',
                        },
                        {
                            value: 'Xylazine',
                            label: 'Xylazine',
                        },
                    ]}
                    allowClear
                />
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead className={styles.stickyHeader}>
                            <tr>
                                <th width={'40%'} onClick={() => setSortingKey('label')}>Substance</th>
                                <th width={'15%'} onClick={() => setSortingKey('modelAverage')}>MA</th>
                                <th width={'15%'} onClick={() => setSortingKey('model1')}>M1</th>
                                <th width={'15%'} onClick={() => setSortingKey('model2')}>M2</th>
                                <th width={'15%'} onClick={() => setSortingKey('trace')}>T</th>
                            </tr>
                        </thead>
                        <tbody>
                            {predictions.map((e, i) => (
                                <tr key={e.label} style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : 'transparent' }}>
                                    <td>{e.label ?? '-'}</td>
                                    <td>{e.modelAverage?.toFixed(3) ?? '-'}</td>
                                    <td>{e.model1?.toFixed(3) ?? '-'}</td>
                                    <td>{e.model2?.toFixed(3) ?? '-'}</td>
                                    <td>{e.trace?.toFixed(3) ?? '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        },

    ];

    return (
        <>
            {isInspectionModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    zIndex: 100,
                    pointerEvents: 'none'
                }}
                >

                    <Rnd
                        style={{
                            pointerEvents: 'auto',
                            borderRadius: '15px',
                            position: 'relative',
                        }}
                        size={{ width: modalWindowState.width, height: modalWindowState.height }}
                        position={{ x: modalWindowState.x, y: modalWindowState.y }}
                        onDragStop={(e, d) => {
                            setModalWindowState(prev => ({ ...prev, x: d.x, y: d.y }))
                        }}
                        onResize={(e, direction, ref, delta, position) => {
                            setModalWindowState(prev => ({
                                ...prev,
                                // width: ref.style.width,
                                height: ref.style.height,
                                ...position
                            }));
                        }}
                        lockAspectRatio={true}
                        enableResizing={{
                            bottom: true,
                        }}
                        minHeight={360}
                        maxHeight={maxHeight}
                    >
                        <div
                            style={{
                                backgroundColor: '#f5f5f5',
                                position: 'relative',
                                top: '0px',
                                left: '0px',
                                width: `calc(${modalWindowState.width})`,
                                height: `calc(${modalWindowState.height})`,
                                zIndex: 101,
                                borderRadius: '15px',
                                padding: '15px'
                            }}>
                            <div
                                style={{
                                    width: '100%',
                                    height: `calc(${modalWindowState.height} - 90px)`,
                                    marginTop: '24px',
                                    // overflow: 'auto',
                                }}
                                className={styles.popup}
                            >
                                <div ref={descriptionsComponentRef} style={{ height: '200px' }}>
                                    <Descriptions
                                        layout="vertical" bordered
                                        items={items}
                                        size="small"
                                        labelStyle={{ fontSize: '12px' }}
                                        contentStyle={{ fontSize: '12px', lineHeight: '9px', height: '9px' }}
                                        style={{ height: '200px' }} />
                                </div>
                            </div>
                        </div>
                        <Button
                            type="primary"
                            danger
                            shape="circle"
                            size="small"
                            icon={<CloseOutlined style={{ fontSize: '9px' }} />}
                            onClick={() => {
                                setIsInspectionModalOpen(false);
                            }}
                            style={{
                                position: 'fixed',
                                top: '18px',
                                left: '18px',
                                minWidth: '15px',
                                width: '15px',
                                height: '15px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 103,
                            }}
                        />
                    </Rnd>
                </div>
            )}
        </>
    )
}

export default InspectionModal;